/* eslint-disable no-shadow */
import Vue from 'vue';
import { i18n } from '@/plugins/vue-i18n';
import { router } from '@/plugins/vue-router';
import InitProxy from '@/proxies/InitProxy';
import AuthProxy from '@/proxies/AuthProxy';
import I18nProxy from '@/proxies/I18nProxy';
import LogoutProxy from '@/proxies/LogoutProxy';
import * as routes from '@route-types';
import { LOGIN, LOGOUT } from '@route-types';

export const getDefaultState = () => ({
  sessionId: null,
  availableLanguages: null,
  availableCrms: [],
  targetView: null,
  isFirstCall: null,
  isDeeplink: false,
  deeplinkSubscriptionTemplateId: null,
  deeplinkUniqueSubscriptionId: null,
  deeplinkNewSubscriptionTemplateId: null,

  natco: null,
  environment: null,
  pageContext: 'SPACE',
  authType: 'NONE',
  entranceType: null,
  features: {},
  flatLinkLimit: null,
  firstExternalLoginStatusCallerUrl: null,
  secondExternalLoginStatusCallerUrl: null,
  tealiumURL: null,
  isEnableWebtrekk: false,
  webtrekkFixedContentDomain: null,
  externalSearchUrl: null,
  wholesalePartnerId: -1, // initialize to -1 to suppress magenta header until /api/init returns with correct ID.
  hasLogo: false,
  errorRedirectionTime: null,
  refUrl: null,
  externalContractManagementUrl: null,
  externalPasswordChangeUrl: null,

  username: null,
  firstname: null,
  email: null,
  displayname: null,
  userProfile: null,
  vpNo: null,
  crm: 'NoCRM',
  logoutUrl: null,
  accessRights: null,
  readOnlyAccess: false,
  stockOverviewByCustomerIdentifierSupported: false,

  // internal:
  oldAuthType: null,
  isAuthExpired: false,
  initPromise: null,
  initComplete: false,
});

export const state = getDefaultState();

export const types = {
  RESET: 'RESET',
  INIT: 'INIT',
  DELETE_TARGETVIEW: 'DELETE_TARGETVIEW',
  RESET_DEEPLINK: 'RESET_DEEPLINK',
  SET: 'SET',
  SET_WHOLESALEPARTNERID: 'SET_WHOLESALEPARTNERID',
  SET_HASLOGO: 'SET_HASLOGO',
  SET_AUTH_EXPIRED: 'SET_AUTH_EXPIRED',
};

export const mutations = {
  [types.RESET](state) {
    let features = undefined;
    if (state.features && state.features.showPasswordChangeForm) {
      features = { showPasswordChangeForm: state.features.showPasswordChangeForm };
    }
    Object.assign(state, getDefaultState(), {
      environment: state.environment,
      natco: state.natco,
      availableCrms: state.availableCrms,
      wholesalePartnerId: state.wholesalePartnerId, // don't reset wholesalePartnerId to suppress flashing colors on logout
      oldAuthType: state.authType,
      initPromise: state.initPromise,
      initComplete: state.initComplete,
      features,
    });

    const templates = localStorage.getItem('templates');
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem('templates', templates);

    Vue.$http.defaults.headers.common.sessionId = '';
    Vue.$http.defaults.headers.common.customerHash = '';
    Vue.$http.defaults.headers.common.contractHash = '';
  },

  [types.INIT](state, initPromise) {
    state.initPromise = initPromise;
  },

  [types.DELETE_TARGETVIEW](state) {
    state.targetView = null;
  },

  [types.RESET_DEEPLINK](state) {
    state.isDeeplink = false;
    state.deeplinkSubscriptionTemplateId = null;
    state.deeplinkUniqueSubscriptionId = null;
    state.deeplinkNewSubscriptionTemplateId = null;
  },

  [types.SET](state, initResponse) {
    Object.entries(getDefaultState())
      .filter(([k, v]) => k !== 'initPromise')
      .forEach(([k, v]) => {
        state[k] = initResponse[k] !== undefined ? initResponse[k] : v; // NOTE: this will reset internal state (i.e. oldAuthType)
      });

    sessionStorage.setItem('sessionId', initResponse.sessionId);

    if (initResponse.deeplink) {
      state.isDeeplink = true;
      state.deeplinkSubscriptionTemplateId = initResponse.deeplink.subscriptionTemplateId;
      state.deeplinkUniqueSubscriptionId = initResponse.deeplink.uniqueSubscriptionId;
      state.deeplinkNewSubscriptionTemplateId = initResponse.deeplink.newSubscriptionTemplateId;
    }

    Vue.$http.defaults.headers.common.sessionId = initResponse.sessionId;
    state.initComplete = true;
  },

  [types.SET_WHOLESALEPARTNERID](state, wholesalePartnerId) {
    state.wholesalePartnerId = wholesalePartnerId;
  },

  [types.SET_HASLOGO](state, hasLogo) {
    state.hasLogo = hasLogo;
  },

  [types.SET_AUTH_EXPIRED](state) {
    state.isAuthExpired = true;
  },
};

const isAuthenticated = (authType) => {
  return authType && authType !== 'NONE';
};

export const getters = {
  isProduction: (state) => !['dev', 'test', 'staging'].includes(state.environment),
  enableBarringProfileOfTypeCategory: (state) => state.features && !!state.features.enableBarringProfileOfTypeCategory,
  enableWebtrekkIntegration: (state) => state.features && !!state.features.enableWebtrekkIntegration,
  showPasswordChangeForm: (state) => state.features && !!state.features.showPasswordChangeForm,
  webtrekkFixedContentDomain: (state) => state.webtrekkFixedContentDomain,
  isTelekom: (state) =>
    state.wholesalePartnerId === null ||
    state.wholesalePartnerId === '0' ||
    state.wholesalePartnerId === '1' ||
    state.wholesalePartnerId === 'undefined' ||
    state.wholesalePartnerId === 'null',
  isLogoAvailable: (state) => state.wholesalePartnerId && state.hasLogo,
  isPageContextSpace: (state) => state.pageContext === 'SPACE',
  isPageContextCita: (state) => state.pageContext === 'CITA',
  isPageContextUnbarring: (state) => state.pageContext === 'UNBARRING',
  isPageContextAuth: (state) => state.pageContext === 'AUTH',

  // @see: de.scoopgmbh.commongui.config.impl.CRMSystem
  isCrmMobile: (state) => state.crm === 'Mobile',
  isCrmFixednet: (state) => state.crm === 'FixedLine',
  isCrmUnified: (state) => state.crm === 'Unified',
  isCrmCable: (state) => state.crm === 'Cable',
  isDashboardVisible: (state) =>
    state.accessRights && (state.accessRights.includes('InformationCustomerData') || state.accessRights.includes('InformationContractData')),

  hasRight: (state, getters) => (fn) => {
    if (typeof fn === 'function') {
      return fn(state);
    }
    if (typeof fn === 'string') {
      return getters[fn];
    }
    return false;
  },

  isAuthenticated: (state) => isAuthenticated(state.authType),
  wasAuthenticated: (state) => isAuthenticated(state.oldAuthType),
  isAuthTypeLogin: (state) => state.authType === 'LOGIN',
  isAuthTypeOpenId: (state) => state.authType === 'OPENID',
  isAuthTypeAuth: (state) => state.authType === 'AUTH',
  isAuthTypeCT: (state) => state.authType === 'CT',
  isOldAuthTypeLogin: (state) => state.oldAuthType === 'LOGIN',

  // @see: de.scoopgmbh.commongui.server.business.api.CrmPermission
  isCustomerSearchAllowed: (state) => state.accessRights && state.accessRights.includes('AccessCustomerSearch'),
  showInformationCustomerData: (state) => state.accessRights && state.accessRights.includes('InformationCustomerData'),
  isContractSearchAllowed: (state) => state.accessRights && state.accessRights.includes('AccessContractSearch'),
  showInformationContractData: (state) => state.accessRights && state.accessRights.includes('InformationContractData'),
  hasAccessCatalogue: (state) => state.accessRights && state.accessRights.includes('AccessProductCatalogue'),
  hasAccessStockOverview: (state) => state.accessRights && state.accessRights.includes('AccessStockOverview'),
  hasAccessPaymentTransactions: (state) => state.accessRights && state.accessRights.includes('AccessPaymentTransactions'),
  hasAccessContentBundles: (state) => state.accessRights && state.accessRights.includes('AccessContentBundles'),
  hasAccessBarringFactoringProfile: (state) => state.accessRights && state.accessRights.includes('AccessBarringProfile'),
  hasAccessBarringResellingProfile: (state) => state.accessRights && state.accessRights.includes('AccessBarringProfileReselling'),
  hasAccessBarringPinProtection: (state) => state.accessRights && state.accessRights.includes('AccessPinProtectionMaintenance'),
  hasShowCCTransactionView: (state) => state.accessRights && state.accessRights.includes('FunctionShowCCTransactionView'),
  hasShowTechnicalInformation: (state) => state.accessRights && state.accessRights.includes('FunctionShowTechnicalInformation'),
  isBarringPinResetAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionBarringProfilePinReset'),
  isBarringShowHistoryAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionBarringProfileShowHistory'),
  isSearchByContractIdentifierAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByContractIdentifier'),
  isSearchByPaymentTransactionIdAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByTransactionId'),
  isSearchByRechargingTransactionIdAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByRechargingTransactionId'),
  isSearchByIdentityAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByIdentity'),
  isSearchByUsidAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByUsid'),
  isSearchByCallnumberAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByCallNumber'),
  isContractSelectionListVisible: (state) => state.accessRights && state.accessRights.includes('FunctionContractSelectionList'),
  hasAccessOrderOverview: (state) => state.accessRights && state.accessRights.includes('AccessOrderOverview'),
  hasAccessInternalCatalogue: (state) => state.accessRights && state.accessRights.includes('AccessInternalCatalogue'),
  hasAccessInternalStock: (state) => state.accessRights && state.accessRights.includes('AccessInternalStockOverview'),
  isSearchByOrderIdAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByOrderId'),
  isSearchByExternalOrderIdAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByExtOrderId'),
  hasAccessPersistentConfirmations: (state) => state.accessRights && state.accessRights.includes('AccessPersistentConfirmations'),
  isModifyPersistentConfirmationAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionModifyPersistentConfirmation'),
  isPerformExternalSearchAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionPerformExternalSearch'),
  hasAccessRechargeTransactions: (state) => state.accessRights && state.accessRights.includes('AccessRechargeTransactions'),
  isSearchByDescKeyAllowed: (state) => state.accessRights && state.accessRights.includes('FunctionSearchByDescKey'),
  hasAccessPayment: (state) => state.accessRights && state.accessRights.includes('AccessPayment'),
  hasAccessAutoTopUpRegistrations: (state) => state.accessRights && state.accessRights.includes('AccessAutoTopUpRegistrations'),
  hasFunctionDeleteAutoTopUpRegistration: (state) => state.accessRights && state.accessRights.includes('FunctionDeleteAutoTopUpRegistration'),
  hasFunctionTerminateNonFinalizedOrder: (state) => state.accessRights && state.accessRights.includes('FunctionTerminateNonFinalizedOrder'),
};

export const actions = {
  setWholesalePartnerId({ commit }, id) {
    commit(types.SET_WHOLESALEPARTNERID, id);
  },
  setHasLogo({ commit }, hasLogo) {
    commit(types.SET_HASLOGO, hasLogo);
  },

  initResume({ commit, dispatch }) {
    // eslint-disable-next-line no-async-promise-executor
    const initPromise = new Promise(async (resolve, reject) => {
      try {
        // NOTE: the init request is already executed in the index.html, if Promises are supported!
        const sessionId = document.sessionId;

        let initResponse;
        if (document.promiseSupport) {
          initResponse = await window.initRequest;
          initResponse = JSON.parse(initResponse);
        } else {
          // fallback, if no promises are supported:
          initResponse = await new InitProxy(sessionId).init();
        }

        await dispatch('set', { initResponse, sessionId });

        resolve(initResponse);
      } catch (e) {
        if (e instanceof XMLHttpRequest) {
          let data;
          try {
            data = JSON.parse(e.response);
          } catch (ignore) {}
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ status: e.status, data });
          return;
        }
        reject(e);
      }
    });

    commit(types.INIT, initPromise);
  },

  async reInit({ state, dispatch }) {
    const initResponse = await new InitProxy(state.sessionId).reInit();
    await dispatch('set', { initResponse });
  },

  async init({ state, dispatch }) {
    const initResponse = await new InitProxy(state.sessionId).init();
    await dispatch('set', { initResponse });
  },

  async set({ commit, dispatch, state, getters }, { initResponse, sessionId = state.sessionId }) {
    if (sessionId !== initResponse.sessionId || initResponse.isFirstCall) {
      // Exclude 'timer' so the running logout countdown is not wiped on customer/contract
      // re-init: isAuthenticated stays true, so the watcher in App.vue would never restart it.
      await dispatch('resetAll', { exclude: ['system', 'timer'] });
    }

    commit(types.SET, initResponse);

    i18n.locale = initResponse.currentLanguage;
    // assign messages:
    if (initResponse.messages) {
      Object.keys(initResponse.messages).forEach((key) => {
        // merging common ensures that the minimum set stays intact
        if (initResponse.messages[key].common) {
          initResponse.messages[key].common = Object.assign({}, i18n.messages[key].common, initResponse.messages[key].common);
        }
        i18n.setLocaleMessage(key, initResponse.messages[key]);
      });
    }

    if (getters.isAuthenticated) {
      if (initResponse.customerHash) {
        dispatch(
          'customer/set',
          {
            customerHash: initResponse.customerHash,
            customerData: initResponse.customerData,
          },
          { root: true }
        );
      }

      if (initResponse.contractHash) {
        dispatch(
          'contract/set',
          {
            contractHash: initResponse.contractHash,
            ...initResponse.contractData,
          },
          { root: true }
        );
      }
    }

    setTimeout(async () => {
      try {
        const i18nResponse = await new I18nProxy().all();
        Object.keys(i18nResponse.messages).forEach((key) => i18n.mergeLocaleMessage(key, i18nResponse.messages[key]));
      } catch (e) {
        router.push({ name: routes.ERROR, params: { error: e } });
      }
    }, 100);

    if (initResponse.error) {
      // eslint-disable-next-line no-throw-literal
      throw { data: initResponse.error };
    }
  },

  resetDeeplink({ commit }) {
    commit(types.RESET_DEEPLINK);
  },

  redirect({ commit, state }) {
    // parse (server side parsed) page parameter for deeplink or returning: (ONLY ONCE!)
    const nextName = state.targetView && state.targetView in routes ? state.targetView : null;

    if (nextName !== null) {
      commit(types.DELETE_TARGETVIEW);
    }

    return nextName;
  },

  setAuthExpired({ commit }) {
    commit(types.SET_AUTH_EXPIRED);
  },

  async loginOpenId({ commit, dispatch }) {
    const response = await new AuthProxy().loginOpenId({ path: location.pathname });

    dispatch('customer/reset', null, { root: true });
    dispatch('contract/reset', null, { root: true });
    commit(types.RESET);

    window.location.href = response.loginUrl;
  },

  async login({ commit, dispatch }, params) {
    dispatch('customer/reset', null, { root: true });
    dispatch('contract/reset', null, { root: true });

    const response = await new AuthProxy().login(params);
    if (response.authType !== 'LOGIN') throw new Error('not authenticated');

    await dispatch('set', { initResponse: response, sessionId: null });

    return response;
  },

  async deleteSessions() {
    try {
      await new LogoutProxy().create(null);
    } catch (e) {
      // ignore
    }
  },

  async logout({ dispatch }) {
    dispatch('deleteSessions'); // without await!
    sessionStorage.setItem('logoutEvent', state.sessionId);
    await dispatch('logoutEvent');
  },

  async logoutEvent({ dispatch, getters, state }) {
    const isAuthTypeLogin = getters.isAuthTypeLogin;
    const logoutUrl = state.logoutUrl;
    await dispatch('resetAll');

    if (isAuthTypeLogin) {
      Vue.router.push({ name: LOGIN });
    } else if (logoutUrl) {
      window.location.href = logoutUrl;
    } else {
      Vue.router.push({ name: LOGOUT });
    }
  },

  resetAll({ commit, rootState }, { exclude = [] } = {}) {
    Object.keys(rootState)
      .filter((module) => !exclude.includes(module))
      .forEach((module) => {
        commit(`${module}/RESET`, null, { root: true });
      });
  },
};
